// src/lib/googleWorkspace.ts
// Utility functions for Google Workspace integration
/**
 * .tuMetrics {"tu1": 0.84, "tu2": 0.85, "tu3": 6, "notes": "Google Workspace helpers; calendar event types match edge-function payloads", "tuVersion": "2.5"}
 * .help "Client helpers for Google Workspace: connection status, calendar events, and Google Doc creation."
 */

import { supabase } from './supabase';
import useUserStore from '@/store/userStore';

// Cache for Google Workspace connection status to prevent excessive requests
// Key: companyId, Value: { isConnected: boolean, timestamp: number }
const connectionCache = new Map<string, { isConnected: boolean; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

/**
 * Check if Google Workspace is connected for the current company
 * Uses caching to prevent excessive database requests
 */
export async function isGoogleWorkspaceConnected(): Promise<boolean> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) return false;

  const companyId = currentUser.companyId;
  const now = Date.now();
  
  // Check cache first
  const cached = connectionCache.get(companyId);
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.isConnected;
  }

  // Cache miss or expired - fetch from database
  try {
    const { data, error } = await supabase
      .from('company_google_workspace_tokens')
      .select('id')
      .eq('company_id', companyId)
      .eq('status', 'active')
      .maybeSingle();

    const isConnected = !error && !!data;
    
    // Update cache
    connectionCache.set(companyId, { isConnected, timestamp: now });
    
    return isConnected;
  } catch (err) {
    console.error('Error checking Google Workspace connection:', err);
    // On error, return false and don't cache
    return false;
  }
}

/**
 * Clear the connection cache (useful when connection status changes)
 */
export function clearGoogleWorkspaceCache(companyId?: string): void {
  if (companyId) {
    connectionCache.delete(companyId);
  } else {
    connectionCache.clear();
  }
}

/**
 * Create a Google Doc from message content
 * @param title - Document title
 * @param content - Content (can be plain text or HTML - will be converted appropriately)
 * @param sessionName - Optional session name
 * @param logoUrl - Optional company logo URL to include at top
 * @param isHtml - Whether content is HTML (default: false, auto-detected if content contains HTML tags)
 * @param footerMetadata - Optional footer metadata (company name, project name, plan name, version)
 * @param planTree - Optional plan tree structure for direct Google Docs API integration
 */
export async function createGoogleDocFromMessage(
  title: string,
  content: string,
  sessionName?: string,
  logoUrl?: string,
  isHtml?: boolean,
  footerMetadata?: {
    company_name?: string;
    plan_id?: string;
    plan_version?: string;
  },
  planTree?: any // PlanTree type from types/planTree.ts
): Promise<{ success: boolean; docUrl?: string; error?: string }> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    // Debug: Log what we're sending
    console.log('📤 createGoogleDocFromMessage called with:', {
      title,
      hasContent: !!content,
      contentLength: content?.length,
      hasPlanTree: !!planTree,
      planTreeKeys: planTree ? Object.keys(planTree) : null,
      planTreeNodesCount: planTree?.nodes ? Object.keys(planTree.nodes).length : null,
    });
    
    const requestBody = {
      action: 'create_doc',
      created_by_user_id: currentUser.id,
      company_id: currentUser.companyId,
      document_title: title,
      document_content: content,
      session_name: sessionName || 'Currents Session',
      logo_url: logoUrl, // Pass logo URL if provided
      is_html: isHtml !== undefined ? isHtml : (content.includes('<') && content.includes('>')), // Auto-detect HTML
      footer_metadata: footerMetadata, // Pass footer metadata if provided
      plan_tree: planTree, // Pass plan tree structure for direct API integration
    };
    
    console.log('📤 Request body plan_tree:', {
      hasPlanTree: !!requestBody.plan_tree,
      planTreeType: typeof requestBody.plan_tree,
      planTreeKeys: requestBody.plan_tree ? Object.keys(requestBody.plan_tree) : null,
    });
    
    // Use dedicated google-docs-integration function (lean microservice)
    console.log(`📤 Calling Edge Function: google-docs-integration`);
    
    const { data, error } = await supabase.functions.invoke('google-docs-integration', {
      body: requestBody,
    });

    if (error) throw error;

    if (data?.success && data?.data?.documentUrl) {
      return { success: true, docUrl: data.data.documentUrl };
    }

    return { success: false, error: data?.error || 'Failed to create document' };
  } catch (err: any) {
    console.error('Error creating Google Doc:', err);
    return { success: false, error: err.message || 'Failed to create document' };
  }
}

/**
 * Create a Google Meet session
 */
import { MeetCreationOptions } from '../features/streams/MeetCreationDialog';

export async function createGoogleMeetSession(
  sessionName: string,
  participants: string[] = [],
  streamType?: string,
  recipientStreamId?: string,
  options?: MeetCreationOptions
): Promise<{ success: boolean; meetUrl?: string; error?: string; spaceName?: string }> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const requestBody = {
      action: 'create_meet',
      created_by_user_id: currentUser.id,
      company_id: currentUser.companyId,
      session_name: sessionName,
      participants: participants,
      external_participants: options?.externalParticipants || [],
      related_entity_type: streamType === 'standup_stream' ? 'chat_session' : streamType === 'direct_message' ? 'chat_session' : null,
      related_entity_id: recipientStreamId || null,
      show_on_team_calendar: options?.showOnTeamCalendar !== false, // Default to true
      is_recurring: options?.isRecurring || false,
      recurrence_pattern: options?.isRecurring ? {
        frequency: options.recurrenceFrequency,
        standard_time: options.standardTime,
        days: options.recurrenceDays,
      } : null,
    };
    
    console.log('📤 Invoking google-meet-integration Edge Function:', requestBody);
    
    const { data, error } = await supabase.functions.invoke('google-meet-integration', {
      body: requestBody,
    });

    console.log('📥 Edge Function response:', { data, error });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw error;
    }

    if (data?.success && data?.data?.meetUrl) {
      return { 
        success: true, 
        meetUrl: data.data.meetUrl,
        spaceName: data.data.spaceName || null, // Include spaceName for API lookups
      };
    }

    return { success: false, error: data?.error || 'Failed to create Meet session' };
  } catch (err: any) {
    console.error('Error creating Google Meet:', err);
    return { success: false, error: err.message || 'Failed to create Meet session' };
  }
}

/**
 * Search Google Contacts using People API
 */
export interface GoogleContact {
  id: string; // e.g., "people/c1234567890"
  name: string;
  email: string;
}

export async function searchGoogleContacts(
  query: string
): Promise<{ success: boolean; contacts?: GoogleContact[]; error?: string }> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) {
    return { success: false, error: 'User not authenticated' };
  }

  if (!query || query.trim().length === 0) {
    return { success: true, contacts: [] };
  }

  try {
    const { data, error } = await supabase.functions.invoke('google-contacts-integration', {
      body: {
        action: 'search_contacts',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        query: query.trim(),
      },
    });

    if (error) {
      console.error('Error searching Google Contacts:', error);
      throw error;
    }

    if (data?.success && data?.contacts) {
      return { success: true, contacts: data.contacts };
    }

    return { success: false, error: data?.error || 'Failed to search Google Contacts' };
  } catch (err: any) {
    console.error('Error searching Google Contacts:', err);
    return { success: false, error: err.message || 'Failed to search Google Contacts' };
  }
}

/**
 * List Google Calendars using Calendar API
 */
export interface GoogleCalendar {
  id: string;
  summary: string;
  description: string;
  timeZone: string;
  primary: boolean;
  accessRole: string;
}

export async function listGoogleCalendars(): Promise<{ success: boolean; calendars?: GoogleCalendar[]; error?: string }> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'list_calendars',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
      },
    });

    if (error) {
      console.error('Error listing Google Calendars:', error);
      throw error;
    }

    if (data?.success && data?.calendars) {
      return { success: true, calendars: data.calendars };
    }

    return { success: false, error: data?.error || 'Failed to list Google Calendars' };
  } catch (err: any) {
    console.error('Error listing Google Calendars:', err);
    return { success: false, error: err.message || 'Failed to list Google Calendars' };
  }
}

/**
 * List Google Calendar Events using Calendar API
 */
export interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description: string;
  location: string;
  // Google Calendar API format: timed events use dateTime, all-day events use date.
  // The edge function always returns an object here ({ dateTime: null, date: null } fallback).
  start: { dateTime?: string | null; date?: string | null };
  end: { dateTime?: string | null; date?: string | null };
  timeZone: string | null;
  attendees: Array<{
    email: string;
    displayName: string;
    responseStatus: string;
  }>;
  organizer: {
    email: string;
    displayName: string;
  } | null;
  status: string;
  htmlLink: string | null;
  hangoutLink: string | null;
  recurrence: string[] | null;
  /** Series id for an instance of a recurring event (Google `recurringEventId`). */
  recurringEventId?: string | null;
}

export async function listGoogleCalendarEvents(
  calendarId: string = 'primary',
  timeMin?: string,
  timeMax?: string
): Promise<{ success: boolean; events?: GoogleCalendarEvent[]; error?: string }> {
  const { currentUser } = useUserStore.getState();
  if (!currentUser?.companyId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'list_events',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        calendar_id: calendarId,
        time_min: timeMin,
        time_max: timeMax,
      },
    });

    console.log(`📥 [GCAL] Edge Function response for ${calendarId}:`, { 
      hasError: !!error, 
      hasData: !!data,
      dataSuccess: data?.success,
      eventsCount: data?.events?.length || 0,
      dataKeys: data ? Object.keys(data) : []
    });

    if (error) {
      console.error('Error listing Google Calendar Events:', error);
      throw error;
    }

    if (data?.success && data?.events) {
      console.log(`✅ [GCAL] Returning ${data.events.length} events for ${calendarId}`);
      return { success: true, events: data.events };
    }

    console.warn(`⚠️ [GCAL] No events returned for ${calendarId}. Data:`, data);
    return { success: false, error: data?.error || 'Failed to list Google Calendar Events' };
  } catch (err: any) {
    console.error('Error listing Google Calendar Events:', err);
    return { success: false, error: err.message || 'Failed to list Google Calendar Events' };
  }
}

